<template>
  <div v-if="guid && !dragging">
    <div class="loader" v-if="loading">
      <q-spinner-gears color="grey-3" :size="80" />
    </div>
    <div :class="loading ? 'hidden': ''">
      <editor
        :disabled="disabled"
        tinymce-script-src="https://cdnjs.cloudflare.com/ajax/libs/tinymce/4.9.11/tinymce.min.js"
        :init="tinyConfig()"
        :initial-value="savedState"
        @input="broadcast()"
        @onInit="loaded()"
        ref="tm"
        v-model="editorContent">
      </editor>
    </div>
    <q-modal v-if="fileModal" v-model="fileModal">
      <file-select-modal :fileType="'image'" :guid="guid"/>
    </q-modal>

    <!-- Button style modal -->
    <q-modal v-model="buttonModal">
          <q-modal-layout>
            <q-toolbar slot="header" color="tertiary">
              <q-toolbar-title>
                  {{ $store.state.stringsStore.language === 'nl' ? 'Selecteer een stijl' : 'Select button style' }}
              </q-toolbar-title>
              <q-btn
                  flat
                  round
                  dense
                  v-close-overlay
                  icon="close"
              />
            </q-toolbar>

            <div class="q-pa-md button-select">
              <a @click="selectButtonStyle()" class="button">
                <span>{{ buttonText || 'Default' }}</span></a>
              <a @click="selectButtonStyle('color-blue')" class="button button--color-blue">
                <span>{{ buttonText || 'Blue' }}</span></a>
              <a @click="selectButtonStyle('color-bg-alt', 'button--font-color-space-grey')" class="button button--color-bg-alt button--font-color-space-grey">
                <span>{{ buttonText || 'Alt' }}</span></a>
              <a @click="selectButtonStyle('transparent')" class="button button--transparent">
                <span>{{ buttonText || 'Transparent' }}</span></a>
            </div>
          </q-modal-layout>
    </q-modal>
  </div>
</template>

<script>
import EventBus from '@/event-bus';
import _debounce from 'lodash.debounce';
import createUuid from '@/helpers/createUuid';
import FileSelectModal from '@/components/modal/FileSelectModal.vue';

import libraryCommon from '@/mixins/library/common';

const Editor = require('@tinymce/tinymce-vue').default;

export default {
  props: {
    linkList: {
      type: Array,
      default: null,
    },
    source: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    savedState: { // Previous state of the editor if the parent content block has been minimized.
      type: String,
    },
  },

  name: 'richTextEditor',

  components: {
    FileSelectModal,
    Editor,
  },

  mixins: [
    libraryCommon,
  ],

  data() {
    return {
      editor: null,
      buttonModal: false,
      textSelection: '',
      loading: true,
      dragging: false,
      fileModal: false,
      editorContent: '',
      plugins: [
        'wordcount',
        'code',
        'link',
        'lists',
        'anchor',
        'template',
        'media',
        'importcss',
        'table',
        'paste',
      ],
      guid: null,
    };
  },

  watch: {
    linkList() {
      if (!this.editor) {
        return;
      }
      // Manually update the link list
      this.editor.settings.link_list = this.linkList;
    },
  },

  methods: {
    selectButtonStyle(style, fontStyle) {
      if (!this.editor) {
        return;
      }

      this.editor.focus();

      const node = this.editor.selection.getNode();

      // First remove the stylesv
      node.classList.remove(...node.classList);

      node.classList.add('button');

      if (style) {
        node.classList.add(`button--${style}`);
      }
      if (fontStyle) {
        node.classList.add(fontStyle);
      }

      if (!node || node.length === 0) {
        this.buttonModal = false;
        return;
      }

      this.buttonModal = !this.buttonModal;

      // Broadcast the change
      this.broadcast();
    },

    loaded() {
      this.loading = false;
    },

    insertImage(editor) {
      EventBus.$on(`file-select:${this.guid}`, (data) => {
        // Image selected, remove the event listener, close the modal.
        EventBus.$off(`file-select:${this.guid}`);
        this.fileModal = false;

        const range = editor.selection.getRng();
        const newNode = editor.getDoc().createElement('img'); // create img node in the editor
        newNode.src = this.buildFileLocation(data.location); // add src attribute

        if (data.alt) {
          newNode.alt = data.alt; // Conditionally include the meta tag for the image (read from library)
        }

        range.insertNode(newNode);

        // Broadcast the change
        this.broadcast();
      });
      this.fileModal = true;
    },

    insertScript(editor, data, edit = false) {
      // Check if we are editing (the old node will be replaced)
      if (edit) {
        const sel = editor.selection.getNode();
        // Remove the old element
        sel.parentNode.removeChild(sel);
      }

      const range = editor.selection.getRng();

      const wrapperNode = editor.getDoc().createElement('div'); // create div node in the editor
      wrapperNode.className = 'whitelist-js';

      let external = true;

      // Check if the entered text is a valid URL
      try {
        // eslint-disable-next-line no-new
        new URL(data.code);
      } catch (_) {
        external = false;
      }

      const hasScript = str => Boolean(
        new DOMParser().parseFromString(str, 'text/html').querySelector('script'),
      );

      if (hasScript(data.code)) {
        wrapperNode.innerHTML = data.code;
      } else if (external) {
        // eslint-disable-next-line no-useless-escape
        wrapperNode.innerHTML = `<script type="application/javascript" src="${data.code}"><\/script>`;
      } else {
        // eslint-disable-next-line no-useless-escape
        wrapperNode.innerHTML = `<script type="application/javascript">${data.code}<\/script>`;
      }

      // Insert the new or updated script element
      range.insertNode(wrapperNode);

      // Broadcast the change
      this.broadcast();
    },

    insertButton(editor) {
      this.textSelection = editor.selection.getContent({ format: 'text' });
      if (this.textSelection && this.textSelection.length > 0) {
        this.buttonModal = true;
      }
    },

    openScriptManager(editor) {
      // Open window
      editor.windowManager.open({
        title: 'Add script',
        width: 440,
        height: 240,
        body: [
          {
            placeholder: this.$store.state.stringsStore.language === 'nl'
              ? 'Voeg een script toe (of een URL voor een extern script)'
              : 'Enter a script (or the URL of an external script)',
            type: 'textbox',
            multiline: true,
            minHeight: 200,
            name: 'code',
          },
        ],

        onsubmit: (e) => {
          this.insertScript(editor, e.data);
        },
      });
    },

    broadcast: _debounce(function () {
      // Publish changes to the content block. (specific to this instance)
      EventBus.$emit(`tiny-editor:${this.source.guid}`, { source: this.source, content: this.editorContent });
    }, 250),

    tinyConfig() {
      return {
        height: 450,
        branding: false,
        browser_spellcheck: true,
        plugins: this.plugins,
        extended_valid_elements: [
          'script[charset|defer|language|src|type]',
        ],
        toolbar: [
          'undo redo | bullist numlist | bold italic | link image alignleft aligncenter alignright | imageSelect | addScript | addButton | media | code | anchor | table',
        ],
        templates: [
          { title: 'Name', description: 'Users full name', content: `${this.$store.state.meStore.me.name.firstname} ${this.$store.state.meStore.me.name.surname}` },
        ],
        paste_as_text: true,

        link_list: this.$store.state.editorStore.blockIds,

        content_css: '/editor.css',

        setup: (editor) => {
          // Store a reference to the editor object
          this.editor = editor;

          editor.addButton('imageSelect', {
            tooltip: 'Select an image from the library',
            icon: 'books',
            onclick: () => {
              this.insertImage(editor);
            },
          });

          editor.addButton('addButton', {
            text: 'button',
            icon: false,
            onclick: () => {
              this.insertButton(editor);
            },
          });

          editor.on('click', (e) => {
            e = e.target;

            if (e.nodeName === 'DIV' && e.classList && e.classList.contains('whitelist-js')) {
              editor.selection.select(e);

              // Open window
              editor.windowManager.open({
                title: 'Add script',
                width: 440,
                height: 240,
                body: [
                  {
                    placeholder: this.$store.state.stringsStore.language === 'nl'
                      ? 'Voeg een script toe (of een URL voor een extern script)'
                      : 'Enter a script (or the URL of an external script)',
                    type: 'textbox',
                    value: e.firstElementChild.innerHTML,
                    multiline: true,
                    minHeight: 200,
                    name: 'code',
                  },
                ],

                onsubmit: (ev) => {
                  this.insertScript(editor, ev.data, true);
                },
              });
            }
          });

          editor.addButton('addScript', {
            text: '<script>',
            icon: false,
            stateSelector: 'whitelist-js',
            onPostRender() {
              const ctrl = this;

              editor.on('NodeChange', (e) => {
                ctrl.active(e.element.className === 'whitelist-js');
              });
            },
            onclick: () => {
              this.openScriptManager(editor);
            },
          });
        },
        pagebreak_split_block: true,
      };
    },
  },

  computed: {
    buttonText() {
      const formatted = this.textSelection.length > 12 ? `${this.textSelection.substring(0, 9)}...` : this.textSelection;
      return this.textSelection ? formatted : null;
    },
  },

  mounted() {
    EventBus.$on('file-select-close', () => {
      this.imageModal = false;
      this.fileModal = false;
    });
    // Create a unique Id to listen to incoming events for this instance of the image selector.
    this.guid = createUuid();

    // Make sure this component rerenders after dragging content blocks.
    EventBus.$on('content-editor:dragging', (dragging) => {
      this.$nextTick(() => {
        this.dragging = dragging;
      });
    });
  },

  destroyed() {
    EventBus.$off('content-editor:dragging');
    EventBus.$off('file-select-close');
  },
};
</script>

<style lang="stylus">
.hidden
  display none
.loader
  display flex
  justify-content center

.editor-button {
    border: none;
    font-weight: 600;
    box-sizing: border-box;
    max-height: 43px;
    box-shadow: 0 0 4.5px 0 rgba(0, 0, 0, 0.20);
    cursor: pointer;
    border: 1px solid transparent;
    border-radius: 5px;
    padding: 0px 20px;
    line-height: 2.6em;
    font-size: 1em;
}
.editor-button__red {
    background-color: rgb(226, 0, 26);
    color: white;
}
.editor-button__white {
    background-color: white;
    color: black;
}
.editor-button__black {
    background-color: black;
    color: white;
}
.editor-button__grey {
    background-color: rgb(118, 118, 118);
    color: white;
}
.editor-button__lightgrey {
    color: #767676;
    background-color: #f2f2f2;
}
.button-select
  max-width 350px
  display flex
  flex-wrap wrap
  & *
    margin 12px

// Colors.
$lem-white = #ffffff;
$lem-deep-blue = #1E6187;
$lem-deep-blue-darker = darken($lem-deep-blue, 5%);
$lem-dark-grey = #727272;
$lem-light-grey =#D8D8D8;
$lem-orange = #E6AF5F;
$lem-red = #a64452;
$lem-beige = #F1ECD6;
$lem-space-grey = #1E1E1E;
$lem-background = #FEF8E0;
$lem-background-alt = #FFFCEF;
$lem-star-default = #FFEFB1;
$lem-star-active = #5BB059;

.button {
  display: inline-flex;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  padding: 10px;
  background: $lem-orange;
  border-radius: 8px;
  color: $lem-white;
  // @include size--default-1;
  // @include font--primary;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    svg {
      transform: translateX(2px);
      transition: transform 0.11s ease-in;
    }
  }

    // span {
    //     margin-right: toRem(16);
    // }

  svg {
    fill: $lem-white;
    transform: translateX(0);
    transition: transform 0.07s ease-out;
    margin-left: toRem(16);
  }

  // Colors
  &--color-blue {
    background-color: $lem-deep-blue;
  }

  &--color-bg-alt {
    background-color: $lem-background-alt;
  }

  // Button transparent
  &--transparent {
    background: none;
    color: $lem-deep-blue;

    &:hover {
      background-color: rgba($lem-space-grey, .1);
    }

    svg {
      fill: $lem-deep-blue;
    }
  }

  // font-color
  &--font-color-white {
    color: $lem-white;
    svg{
      fill: $lem-white;
    }
  }
  &--font-color-space-grey {
    color: $lem-space-grey;
  }

  // small faq button for showing  hiding the faq's
  &--faq-toggle {
    padding: toRem(17) toRem(15);
    svg {
      margin-left: 0;
      width: toRem(12);
      height: toRem(8);
    }
  }

  // Show more button for the books overview-page.
  &--show-more {
    background-color: rgba($lem-space-grey, .3);
  }

  &--show-more,
  &--filter,
  &--sort {
    &:hover {
      svg {
        -webkit-animation: bounce-down-up 0.22s ease-in both;
        animation: bounce-down-up 0.22s ease-in both;
      }
    }
  }

  &--back {
    &:hover {
      svg {
        transform: translateX(-2px);
        transition: transform 0.11s ease-in;
      }
    }
  }

  // Size of individual icons with an ID
  svg#filter {
    width: toRem(16);
    height: toRem(17);
  }

  svg#chevron-left,
  svg#chevron-right {
    height: toRem(10);
    width: toRem(14);
  }

  svg#chevron-down {
    height: toRem(8);
    width: toRem(12);
  }

  svg#chevron-down {
    height: toRem(8);
    width: toRem(12);
  }

  // Individual styling by ID
  svg#chevron-left {
    margin-right: toRem(10);
    margin-left: 0;
  }

  &--no-style {
    background: none;
    color: inherit;
    border: none;
    padding: 0;
    font: inherit;
    cursor: pointer;
    outline: inherit;
  }
}

</style>
